import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import ScrollToTop from './components/ScrollToTop'
import UpdateNotification from './components/UpdateNotification'
import PageSkeleton from './components/PageSkeleton'
import { useAppVersion } from './hooks/useAppVersion'
import { ThemeProvider } from './lib/theme'
import { UploadProvider } from './lib/uploadContext'
import { VideoPlayerProvider } from './lib/videoPlayerContext'

// Route-based code-splitting: each page is loaded on demand
const Home = lazy(() => import('./pages/home'))
const FeedPage = lazy(() => import('./pages/feed'))
const Login = lazy(() => import('./pages/login'))
const Signup = lazy(() => import('./pages/signup'))
const Explorer = lazy(() => import('./pages/explorer'))
const Users = lazy(() => import('./pages/users'))
const PostDetail = lazy(() => import('./pages/postDetail'))
const Profile = lazy(() => import('./pages/profile'))
const CreatePost = lazy(() => import('./pages/createPost'))
const EditProfile = lazy(() => import('./components/profilePage/EditProfile'))
const ChangePassword = lazy(() => import('./pages/changePassword'))
const ForgotPassword = lazy(() => import('./pages/forgotPassword'))
const VerificationRequest = lazy(() => import('./pages/verificationRequest'))
const ProfileSettings = lazy(() => import('./pages/profileSettings'))
const NotificationsPage = lazy(() => import('./pages/notifications'))
const MessagesPage = lazy(() => import('./pages/messages'))
const ConversationPage = lazy(() => import('./pages/conversation'))
const Reels = lazy(() => import('./pages/Reels'))
const LiveScoresPage = lazy(() => import('./pages/livescores'))
const InstallApp = lazy(() => import('./pages/installApp'))
const CoinsPage = lazy(() => import('./pages/coins'))
const GamesPage = lazy(() => import('./pages/games'))
const GamesGuidePage = lazy(() => import('./pages/gamesGuide'))
const GameDetailPage = lazy(() => import('./pages/gameDetail'))
const CreateGamePage = lazy(() => import('./pages/admin/CreateGame'))
const DeleteGamesPage = lazy(() => import('./pages/admin/DeleteGames'))
const ResolveGamesPage = lazy(() => import('./pages/admin/ResolveGames'))
const ResolveGamePage = lazy(() => import('./pages/admin/ResolveGame'))
const PitchPage = lazy(() => import('./pages/pitch'))
const TransfersPage = lazy(() => import('./pages/transfers'))
const TransferPlayerPage = lazy(() => import('./pages/transferPlayer'))
const TransferTeamPage = lazy(() => import('./pages/transferTeam'))
const AboutPage = lazy(() => import('./pages/about'))
const NewsPage = lazy(() => import('./pages/news'))
const NewsArticlePage = lazy(() => import('./pages/newsArticle'))
const NotFoundPage = lazy(() => import('./pages/notFound'))

function App() {
  const { needsUpdate, currentVersion, clearCacheAndReload, dismissUpdate } = useAppVersion()

  return (
    <ThemeProvider>
      <UploadProvider>
        <VideoPlayerProvider>
          {needsUpdate && (
            <UpdateNotification
              newVersion={currentVersion}
              onReload={clearCacheAndReload}
              onDismiss={dismissUpdate}
            />
          )}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="min-h-screen w-full"
          >
              <ScrollToTop />
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  <Route path="/lenz/reels" element={<Reels />} />
                  <Route path="*" element={
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/feed" element={<FeedPage />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/explorer" element={<Explorer />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/livescores" element={<LiveScoresPage />} />
                      <Route path="/post/:id" element={<PostDetail />} />
                      <Route path="/p/:hash" element={<PostDetail />} />
                      <Route path="/edit-profile" element={<EditProfile />} />
                      <Route path="/verification-request" element={<VerificationRequest />} />
                      <Route path="/profile-settings" element={<ProfileSettings />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/messages" element={<MessagesPage />} />
                      <Route path="/messages/:id" element={<ConversationPage />} />
                      <Route path="/change-password" element={<ChangePassword />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/install" element={<InstallApp />} />
                      <Route path="/coins" element={<CoinsPage />} />
                      <Route path="/coins/leaderboard" element={<Navigate to="/games?tab=leaderboard" replace />} />
                      <Route path="/games" element={<GamesPage />} />
                      <Route path="/games/guide" element={<GamesGuidePage />} />
                      <Route path="/games/:id" element={<GameDetailPage />} />
                      <Route path="/pitch" element={<PitchPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/news" element={<NewsPage />} />
                      <Route path="/news/:slug" element={<NewsArticlePage />} />
                      <Route path="/transfers" element={<TransfersPage />} />
                      <Route path="/transfers/player/:id" element={<TransferPlayerPage />} />
                      <Route path="/transfers/team/:id" element={<TransferTeamPage />} />
                      <Route path="/admin/games/new" element={<CreateGamePage />} />
                      <Route path="/admin/games/delete" element={<DeleteGamesPage />} />
                      <Route path="/admin/games/resolve" element={<ResolveGamesPage />} />
                      <Route path="/admin/games/:id/resolve" element={<ResolveGamePage />} />
                      <Route path="/create" element={<CreatePost />} />
                      <Route path="/:id" element={<Profile />} />
                      {/* Catch-all for unmatched multi-segment paths (e.g. /foo/bar) — emits noindex */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  } />
                </Routes>
              </Suspense>
          </motion.div>
        </VideoPlayerProvider>
      </UploadProvider>
    </ThemeProvider>
  )
}

export default App
